import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { track } from '@amplitude/analytics-browser';
import { Box, Button, Icon, Link } from '@nimbus-ds/components';
import {
  CalculatorIcon,
  ExternalLinkIcon,
  InfoCircleIcon,
} from '@nimbus-ds/icons';
import { InitialScreen } from '@nimbus-ds/patterns';
import { BaseAppLayout } from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import { InstallationStatusAlert } from 'commons/components';
import {
  useFeatureFlag,
  useIsMobile,
  useMexicoEmptyState,
} from 'commons/hooks';
import { FeatureFlag } from 'commons/services';
import { useTranslationWithPrefix } from 'commons/utils';
import { BenefitsSection } from './BenefitsSection';
import { FaqAccordionSection } from './FaqAccordionSection';
// import { FaqVideoGuidesSection } from './FaqVideoGuidesSection';
import { FeatureCardsSection } from './FeatureCardsSection';
import { FeeCalculatorModal } from './FeeCalculatorModal';
import { FeeCalculatorSection } from './FeeCalculatorSection';

const EmptyStateMexico = () => {
  const { t } = useTranslationWithPrefix('landing');

  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [isInstallationStatusAlertShow, setIsInstallationStatusAlertShow] =
    useState(true);

  const { emptyState, isLoading, shouldRedirectHome } = useMexicoEmptyState();
  const isMobile = useIsMobile();
  const { enabled: feeCalculatorEnabled } = useFeatureFlag(
    FeatureFlag.FS_PAGONUBE_FEE_CALCULATOR_ENABLED,
  );

  useEffect(() => {
    track('tiendanube_mx_pagonube_landing_view');
  }, []);

  if (shouldRedirectHome) {
    return <Redirect to={ROUTES.HOME} />;
  }

  if (isLoading) {
    return <></>;
  }

  const handleOpenCalculatorModal = () => {
    track('tiendanube_mx_pagonube_landing_calculator_click');
    setIsCalculatorOpen(true);
  };

  const handleRemoveInstallationStatusAlert = () => {
    setIsInstallationStatusAlertShow(false);
  };

  return (
    <BaseAppLayout>
      <div data-testid="empty-mexico">
        <Box maxWidth="1200px" margin="auto" padding="2" width="100%">
          {emptyState.alertEnabled && (
            <Box marginBottom="8">
              <InstallationStatusAlert
                status={emptyState.status}
                show={!!emptyState.status && isInstallationStatusAlertShow}
                requestDate={emptyState.requestDate}
                messageCode={emptyState.messageCode}
                messageSeverity={emptyState.messageSeverity}
                nextActionCode={emptyState.nextActionCode}
                deadline={emptyState.deadline}
                handleClick={emptyState.handleClick}
                onRemove={handleRemoveInstallationStatusAlert}
              />
            </Box>
          )}
          <InitialScreen.Hero
            title={t('title')}
            subtitle={t('subtitle')}
            description={t('description')}
            actions={
              <Box
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                gap="4"
                width="100%"
              >
                {emptyState.isBtnEnabled && (
                  <Button
                    appearance="primary"
                    onClick={emptyState.handleClick}
                    fullWidth={isMobile}
                  >
                    {t(`${emptyState.status}.primaryBtn`)}
                  </Button>
                )}
                {feeCalculatorEnabled && (
                  <Button
                    appearance="neutral"
                    onClick={handleOpenCalculatorModal}
                    fullWidth={isMobile}
                  >
                    <Icon
                      color="currentColor"
                      source={<CalculatorIcon size="small" />}
                    />
                    {t('calculatorButton')}
                  </Button>
                )}
              </Box>
            }
            image={
              <Box maxWidth="400px" margin="auto">
                <img
                  src={'/assets/img/landing_mexico_1.svg'}
                  width="100%"
                  height="auto"
                  alt="Empty state image"
                />
              </Box>
            }
          />
          <FeatureCardsSection />
          <BenefitsSection />
          <FeeCalculatorSection
            showPrimaryAction={emptyState.isBtnEnabled}
            primaryActionLabel={t(`${emptyState.status}.primaryBtn`)}
            onPrimaryAction={emptyState.handleClick}
            secondaryActionLabel={t('calculatorButton')}
            onSecondaryAction={() => setIsCalculatorOpen(true)}
            showSecondaryAction={feeCalculatorEnabled}
          />
          {/* TODO: Add back when we have the video guides */}
          {/* <FaqVideoGuidesSection /> */}
          <FaqAccordionSection />
          <Box marginTop="8" display="flex" justifyContent="center">
            <Link
              as="a"
              appearance="primary"
              href={t('moreAboutPagoNube.href')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track('tiendanube_mx_pagonube_landing_more_info_click')
              }
            >
              <Icon color="currentColor" source={<InfoCircleIcon />} />
              {t('moreAboutPagoNube.text')}
              <Icon color="currentColor" source={<ExternalLinkIcon />} />
            </Link>
          </Box>
        </Box>
      </div>
      {feeCalculatorEnabled && (
        <FeeCalculatorModal
          isOpen={isCalculatorOpen}
          onClose={() => setIsCalculatorOpen(false)}
        />
      )}
    </BaseAppLayout>
  );
};

export default EmptyStateMexico;
